import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp, Calculator, RefreshCw, Plus, Edit2, Trash2, Save, X,
  CheckCircle2, AlertTriangle, Info, BarChart3, PlayCircle, Eye, 
  ChevronLeft, FileText, ArrowUpDown, BookOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "../../../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;
const T = "cao";
const MN = {"01":"Ene","02":"Feb","03":"Mar","04":"Abr","05":"May","06":"Jun","07":"Jul","08":"Ago","09":"Sep","10":"Oct","11":"Nov","12":"Dic"};
const fmtN = (v) => new Intl.NumberFormat("es-AR",{maximumFractionDigits:2}).format(v);
const fmtC = (v) => new Intl.NumberFormat("es-AR",{style:"currency",currency:"ARS",minimumFractionDigits:2}).format(v||0);
const fmtP = (p) => { if(!p)return""; const[y,m]=p.split("-"); return `${MN[m]||m} ${y}`; };
const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#ec4899","#06b6d4","#f97316"];
const TIPO_COLOR = {ACTIVO:"#3b82f6",PASIVO:"#ef4444",PATRIMONIO:"#8b5cf6",INGRESO:"#10b981",EGRESO:"#f59e0b"};

const AjusteInflacion = () => {
  const [loading,setLoading] = useState(true);
  const [activeTab,setActiveTab] = useState("indices");
  const [indices,setIndices] = useState([]);
  const [cuentas,setCuentas] = useState([]);
  const [resumen,setResumen] = useState(null);
  const [ejercicios,setEjercicios] = useState([]);
  const [resultados,setResultados] = useState([]);
  const [resultadoActual,setResultadoActual] = useState(null);
  const [vistaResultado,setVistaResultado] = useState(false);
  const [modalIndice,setModalIndice] = useState(false);
  const [editingIndice,setEditingIndice] = useState(null);
  const [formIndice,setFormIndice] = useState({periodo:"",indice:"",variacion_mensual:""});
  const [saving,setSaving] = useState(false);
  const [calculando,setCalculando] = useState(false);
  const [generandoAsiento,setGenerandoAsiento] = useState(false);
  const [cuentasModificadas,setCuentasModificadas] = useState({});
  const [ejercicioSeleccionado,setEjercicioSeleccionado] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [indR,resR,ctaR,ejR,resltR] = await Promise.all([
        axios.get(`${API}/api/erp/empresas/${T}/indices-facpce`),
        axios.get(`${API}/api/erp/empresas/${T}/rt54-resumen`),
        axios.get(`${API}/api/erp/empresas/${T}/cuentas-clasificacion`),
        axios.get(`${API}/api/erp/empresas/${T}/ejercicios`),
        axios.get(`${API}/api/erp/empresas/${T}/rt54/resultados`),
      ]);
      setIndices(indR.data); setResumen(resR.data); setCuentas(ctaR.data);
      setEjercicios(ejR.data); setResultados(resltR.data);
    } catch(e) { console.error(e); toast.error("Error cargando datos"); }
    setLoading(false);
  },[]);

  useEffect(()=>{fetchAll();},[fetchAll]);

  // === INDICES ===
  const handleGuardarIndice = async () => {
    if(!formIndice.periodo||!formIndice.indice){toast.error("Complete período e índice");return;}
    setSaving(true);
    try {
      if(editingIndice) {
        await axios.put(`${API}/api/erp/empresas/${T}/indices-facpce/${editingIndice.id}`,{
          indice:parseFloat(formIndice.indice), variacion_mensual:formIndice.variacion_mensual?parseFloat(formIndice.variacion_mensual):null});
        toast.success("Índice actualizado");
      } else {
        await axios.post(`${API}/api/erp/empresas/${T}/indices-facpce`,{
          periodo:formIndice.periodo, indice:parseFloat(formIndice.indice),
          variacion_mensual:formIndice.variacion_mensual?parseFloat(formIndice.variacion_mensual):null});
        toast.success("Índice agregado");
      }
      setModalIndice(false); setEditingIndice(null);
      setFormIndice({periodo:"",indice:"",variacion_mensual:""}); fetchAll();
    } catch(e){toast.error(e.response?.data?.detail||"Error");}
    setSaving(false);
  };

  const handleEliminarIndice = async(id) => {
    if(!window.confirm("¿Eliminar este índice?"))return;
    try{await axios.delete(`${API}/api/erp/empresas/${T}/indices-facpce/${id}`);toast.success("Eliminado");fetchAll();}
    catch(e){toast.error("Error");}
  };

  // === CUENTAS ===
  const toggleMonetaria = (cid,v) => setCuentasModificadas(p=>({...p,[cid]:v}));
  const getCuentaMonetaria = (c) => cuentasModificadas.hasOwnProperty(c.id)?cuentasModificadas[c.id]:c.es_monetaria;
  const guardarClasificacion = async () => {
    const cambios = Object.entries(cuentasModificadas).map(([id,v])=>({cuenta_id:parseInt(id),es_monetaria:v}));
    if(!cambios.length){toast.info("Sin cambios");return;}
    setSaving(true);
    try{await axios.put(`${API}/api/erp/empresas/${T}/cuentas-clasificacion-bulk`,{cambios});
    toast.success(`${cambios.length} cuentas actualizadas`);setCuentasModificadas({});fetchAll();}
    catch(e){toast.error("Error guardando");}
    setSaving(false);
  };

  // === CÁLCULO ===
  const ejecutarCalculo = async () => {
    if(!ejercicioSeleccionado){toast.error("Seleccione un ejercicio");return;}
    setCalculando(true);
    try {
      const res = await axios.post(`${API}/api/erp/empresas/${T}/rt54/calcular`,{ejercicio_id:parseInt(ejercicioSeleccionado)});
      toast.success(`Cálculo RT54 completado. RECPAM: ${fmtC(res.data.recpam)}`);
      setResultadoActual(res.data); setVistaResultado(true); fetchAll();
    } catch(e){toast.error(e.response?.data?.detail||"Error en cálculo");}
    setCalculando(false);
  };

  const verResultado = async(id) => {
    try {
      const res = await axios.get(`${API}/api/erp/empresas/${T}/rt54/resultados/${id}`);
      const d = res.data.detalle ? {...res.data.detalle, resultado_id: res.data.id, asiento_id: res.data.asiento_id, estado_resultado: res.data.estado, recpam: res.data.recpam, total_ajuste_no_monetarias: res.data.total_ajuste_no_monetarias, total_cuentas_afectadas: (res.data.detalle.cuentas||[]).length} : res.data; setResultadoActual(d); setVistaResultado(true);
    } catch(e){toast.error("Error cargando resultado");}
  };

  const generarAsiento = async (resultadoId) => {
    if(!window.confirm("¿Generar asiento contable de ajuste por inflación? Esta acción confirmará el resultado.")) return;
    setGenerandoAsiento(true);
    try {
      const res = await axios.post(`${API}/api/erp/empresas/${T}/rt54/resultados/${resultadoId}/generar-asiento`);
      toast.success(res.data.message);
      setVistaResultado(false); fetchAll();
    } catch(e) { toast.error(e.response?.data?.detail || "Error generando asiento"); }
    setGenerandoAsiento(false);
  };

  const hayCambios = Object.keys(cuentasModificadas).length>0;
  const idxActual = resumen?.indice_actual;
  const indicesChart = [...indices].reverse().map(i=>({periodo:fmtP(i.periodo),indice:i.indice,variacion:i.variacion_mensual||0}));

  if(loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-blue-500"/></div>;

  // === VISTA RESULTADO ===
  if(vistaResultado && resultadoActual) {
    const data = resultadoActual;
    const ctas = data.cuentas || [];
    const porTipo = data.resumen_por_tipo || data.resumen_por_tipo || [];
    const recpam = data.recpam || 0;
    const tipoRecpam = recpam > 0 ? "PÉRDIDA" : recpam < 0 ? "GANANCIA" : "NEUTRO";
    const ajusteTotal = data.total_ajuste_no_monetarias || 0;

    return (
      <div className="space-y-6 p-2" data-testid="rt54-resultado">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={()=>setVistaResultado(false)} data-testid="btn-volver-rt54">
              <ChevronLeft className="h-4 w-4 mr-1"/>Volver
            </Button>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Resultado Ajuste RT54</h2>
              <p className="text-sm text-slate-500">{data.ejercicio} | Cierre: {fmtP(data.periodo_cierre)} | Índice: {fmtN(data.indice_cierre)}</p>
            </div>
          </div>
          {data.resultado_id && !data.asiento_id && recpam !== 0 && (
            <Button onClick={()=>generarAsiento(data.resultado_id)} disabled={generandoAsiento} data-testid="btn-generar-asiento-rt54" className="bg-green-600 hover:bg-green-700">
              {generandoAsiento?<RefreshCw className="h-4 w-4 animate-spin mr-2"/>:<BookOpen className="h-4 w-4 mr-2"/>}
              {generandoAsiento?"Generando...":"Generar Asiento de Ajuste"}
            </Button>
          )}
          {data.asiento_id && (
            <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1" data-testid="badge-asiento-generado">
              Asiento #{data.asiento_id} generado
            </Badge>
          )}
        </div>

        {/* KPIs resultado */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Movimientos Procesados</p>
            <p className="text-2xl font-bold text-slate-800">{data.total_movimientos}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Cuentas Afectadas</p>
            <p className="text-2xl font-bold text-blue-600">{data.total_cuentas_afectadas || ctas.length}</p>
          </CardContent></Card>
          <Card className={ajusteTotal !== 0 ? "border-blue-200" : ""}><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Ajuste No Monetarias</p>
            <p className="text-lg font-bold text-blue-600">{fmtC(ajusteTotal)}</p>
          </CardContent></Card>
          <Card className={recpam>0?"border-red-200":recpam<0?"border-green-200":""}><CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">RECPAM</p>
            <p className={`text-lg font-bold ${recpam>0?'text-red-600':recpam<0?'text-green-600':'text-slate-600'}`}>{fmtC(recpam)}</p>
            <Badge className={`text-xs mt-1 ${recpam>0?'bg-red-100 text-red-700':recpam<0?'bg-green-100 text-green-700':'bg-slate-100 text-slate-700'}`}>
              {tipoRecpam}
            </Badge>
          </CardContent></Card>
        </div>

        {/* Info RECPAM */}
        <Alert className="border-slate-200 bg-slate-50">
          <Info className="w-4 h-4 text-slate-600"/>
          <AlertTitle className="text-slate-800">RECPAM - Resultado por Exposición al Cambio en el Poder Adquisitivo</AlertTitle>
          <AlertDescription className="text-slate-600 text-sm">
            Mide la ganancia o pérdida por mantener activos y pasivos monetarios durante un período inflacionario.
            RECPAM positivo = pérdida (los activos monetarios perdieron poder adquisitivo).
            RECPAM negativo = ganancia (las deudas se licuaron por inflación). Referencia: RT54 §186-190.
          </AlertDescription>
        </Alert>

        {/* Resumen por tipo */}
        {porTipo.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Resumen por Tipo de Cuenta</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table data-testid="tabla-resumen-tipo">
                  <TableHeader><TableRow>
                    <TableHead>Tipo</TableHead><TableHead className="text-right">Histórico</TableHead>
                    <TableHead className="text-right">Ajustado</TableHead><TableHead className="text-right">Diferencia</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {porTipo.map((t,i)=>(
                      <TableRow key={i}>
                        <TableCell><Badge style={{backgroundColor:TIPO_COLOR[t.tipo]+'20',color:TIPO_COLOR[t.tipo]}}>{t.tipo}</Badge></TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtC(t.historico)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtC(t.ajustado)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-medium">
                          <span className={t.ajuste!==0?'text-blue-600':'text-slate-400'}>{fmtC(t.ajuste)}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Composición por Tipo</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={porTipo.map(t=>({...t,abs:Math.abs(t.historico)}))} dataKey="abs" nameKey="tipo" cx="50%" cy="50%" outerRadius={80} innerRadius={40}>
                      {porTipo.map((t,i)=><Cell key={i} fill={TIPO_COLOR[t.tipo]||COLORS[i]}/>)}
                    </Pie>
                    <Tooltip formatter={(v)=>fmtC(v)}/>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detalle por cuenta */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Detalle por Cuenta</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[400px] overflow-y-auto">
              <Table data-testid="tabla-detalle-cuentas">
                <TableHeader className="sticky top-0 bg-white z-10"><TableRow>
                  <TableHead>Código</TableHead><TableHead>Cuenta</TableHead><TableHead>Tipo</TableHead>
                  <TableHead className="text-center">Clasif.</TableHead>
                  <TableHead className="text-right">Histórico</TableHead><TableHead className="text-right">Ajustado</TableHead>
                  <TableHead className="text-right">Ajuste</TableHead><TableHead className="text-center">Mov.</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {ctas.map((c,i)=>(
                    <TableRow key={i} className={c.ajuste!==0?'bg-blue-50':''}>
                      <TableCell className="font-mono text-sm">{c.codigo}</TableCell>
                      <TableCell className="text-sm font-medium">{c.descripcion}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{c.tipo_cuenta}</Badge></TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs ${c.es_monetaria?'bg-slate-100 text-slate-600':'bg-blue-100 text-blue-700'}`}>
                          {c.es_monetaria?'Monetaria':'No Monet.'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtC(c.saldo_historico)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtC(c.saldo_ajustado)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-medium">
                        <span className={c.ajuste!==0?'text-blue-600':'text-slate-400'}>{fmtC(c.ajuste)}</span>
                      </TableCell>
                      <TableCell className="text-center text-sm">{c.movimientos}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // === VISTA PRINCIPAL ===
  return (
    <div className="space-y-6 p-2" data-testid="ajuste-inflacion-rt54">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <TrendingUp className="h-7 w-7 text-blue-600"/>Ajuste por Inflación
          </h1>
          <p className="text-sm text-slate-500 mt-1">RT N° 54 - FACPCE | Reexpresión en moneda homogénea</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className="h-4 w-4"/></Button>
      </div>

      <Alert className={resumen?.es_obligatorio?"border-red-200 bg-red-50":"border-blue-200 bg-blue-50"}>
        <Info className={`w-4 h-4 ${resumen?.es_obligatorio?'text-red-600':'text-blue-600'}`}/>
        <AlertTitle className={resumen?.es_obligatorio?"text-red-800":"text-blue-800"}>RT 54 - Norma Unificada (FACPCE)</AlertTitle>
        <AlertDescription className={resumen?.es_obligatorio?"text-red-700":"text-blue-700"}>
          Reexpresión obligatoria cuando inflación acumulada &gt; 100%.
          {resumen?.inflacion_acumulada!=null&&<> Inflación acumulada: <strong>{resumen.inflacion_acumulada}%</strong> — <strong>{resumen.es_obligatorio?"OBLIGATORIA":"No requerida"}</strong></>}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600"/></div>
          <div><p className="text-xs text-slate-500">Índice Actual</p><p className="font-bold text-lg">{fmtN(idxActual?.indice||0)}</p><p className="text-xs text-slate-400">{fmtP(idxActual?.periodo)}</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-orange-600"/></div>
          <div><p className="text-xs text-slate-500">Var. Mensual</p><p className="font-bold text-lg">{idxActual?.variacion_mensual||0}%</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-purple-600"/></div>
          <div><p className="text-xs text-slate-500">Var. Interanual</p><p className="font-bold text-lg">{idxActual?.variacion_anual||0}%</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${resumen?.es_obligatorio?'bg-red-100':'bg-green-100'} flex items-center justify-center`}>
            <AlertTriangle className={`w-5 h-5 ${resumen?.es_obligatorio?'text-red-600':'text-green-600'}`}/></div>
          <div><p className="text-xs text-slate-500">Inflación Acum.</p><p className={`font-bold text-lg ${resumen?.es_obligatorio?'text-red-600':'text-green-600'}`}>{resumen?.inflacion_acumulada||0}%</p></div>
        </div></CardContent></Card>
        <Card><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Calculator className="w-5 h-5 text-slate-600"/></div>
          <div><p className="text-xs text-slate-500">Ctas. No Monet.</p><p className="font-bold text-lg">{resumen?.cuentas?.no_monetarias||0}</p><p className="text-xs text-slate-400">de {resumen?.cuentas?.total||0}</p></div>
        </div></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="indices" data-testid="tab-indices">Índices ({indices.length})</TabsTrigger>
          <TabsTrigger value="cuentas" data-testid="tab-cuentas">Clasificación</TabsTrigger>
          <TabsTrigger value="calculo" data-testid="tab-calculo">Cálculo RT54</TabsTrigger>
          <TabsTrigger value="evolucion" data-testid="tab-evolucion">Evolución</TabsTrigger>
        </TabsList>

        {/* INDICES */}
        <TabsContent value="indices" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><div className="flex items-center justify-between">
              <div><CardTitle className="text-base">Tabla de Índices FACPCE</CardTitle><CardDescription>IPC Nacional INDEC</CardDescription></div>
              <Button size="sm" onClick={()=>{setEditingIndice(null);setFormIndice({periodo:"",indice:"",variacion_mensual:""});setModalIndice(true);}} data-testid="btn-agregar-indice">
                <Plus className="h-4 w-4 mr-1"/>Agregar
              </Button>
            </div></CardHeader>
            <CardContent className="p-0"><div className="max-h-[450px] overflow-y-auto">
              <Table data-testid="tabla-indices"><TableHeader className="sticky top-0 bg-white z-10"><TableRow>
                <TableHead>Período</TableHead><TableHead className="text-right">Índice</TableHead>
                <TableHead className="text-right">Var. Mensual</TableHead><TableHead className="text-right">Var. Interanual</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>{indices.map(idx=>(
                <TableRow key={idx.id}>
                  <TableCell className="font-medium">{fmtP(idx.periodo)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtN(idx.indice)}</TableCell>
                  <TableCell className="text-right">{idx.variacion_mensual!=null&&<Badge className={idx.variacion_mensual>3?"bg-red-100 text-red-700":idx.variacion_mensual>2?"bg-amber-100 text-amber-700":"bg-green-100 text-green-700"}>{idx.variacion_mensual}%</Badge>}</TableCell>
                  <TableCell className="text-right">{idx.variacion_anual!=null&&<Badge variant="outline">{idx.variacion_anual}%</Badge>}</TableCell>
                  <TableCell className="text-right"><div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={()=>{setEditingIndice(idx);setFormIndice({periodo:idx.periodo,indice:String(idx.indice),variacion_mensual:idx.variacion_mensual?String(idx.variacion_mensual):""});setModalIndice(true);}}><Edit2 className="h-3.5 w-3.5"/></Button>
                    <Button variant="ghost" size="sm" onClick={()=>handleEliminarIndice(idx.id)}><Trash2 className="h-3.5 w-3.5 text-red-500"/></Button>
                  </div></TableCell>
                </TableRow>
              ))}</TableBody></Table>
            </div>
            <div className="p-3 bg-slate-50 border-t text-xs text-slate-500"><strong>Fuente:</strong> FACPCE / IPC INDEC</div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CLASIFICACIÓN */}
        <TabsContent value="cuentas" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><div className="flex items-center justify-between">
              <div><CardTitle className="text-base">Clasificación RT54</CardTitle>
                <CardDescription>Monetarias (no se ajustan) vs No monetarias (se ajustan). §176-200</CardDescription></div>
              {hayCambios&&<div className="flex gap-2">
                <Badge className="bg-amber-100 text-amber-700">{Object.keys(cuentasModificadas).length} cambios</Badge>
                <Button size="sm" variant="outline" onClick={()=>setCuentasModificadas({})}><X className="h-4 w-4 mr-1"/>Descartar</Button>
                <Button size="sm" onClick={guardarClasificacion} disabled={saving} data-testid="btn-guardar-clasif"><Save className="h-4 w-4 mr-1"/>Guardar</Button>
              </div>}
            </div></CardHeader>
            <CardContent className="p-0">
              <div className="p-3 bg-blue-50 border-b text-sm text-blue-700">
                <Info className="h-4 w-4 inline mr-1"/>
                <strong>Monetarias</strong>: Caja, Bancos, Créditos, Deudas (generan RECPAM).
                <strong className="ml-2">No Monetarias</strong>: Bienes de Uso, Capital, Resultados (se ajustan por coeficiente).
              </div>
              <div className="max-h-[400px] overflow-y-auto">
              <Table><TableHeader className="sticky top-0 bg-white z-10"><TableRow>
                <TableHead>Código</TableHead><TableHead>Descripción</TableHead><TableHead>Tipo</TableHead>
                <TableHead className="text-center">Monetaria</TableHead><TableHead className="text-center">Se Ajusta</TableHead>
              </TableRow></TableHeader>
              <TableBody>{cuentas.map(c=>{
                const em=getCuentaMonetaria(c); const mod=cuentasModificadas.hasOwnProperty(c.id);
                return(<TableRow key={c.id} className={mod?"bg-amber-50":""}>
                  <TableCell className="font-mono text-sm">{c.codigo}</TableCell>
                  <TableCell className="text-sm">{c.descripcion}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{c.tipo_cuenta}</Badge></TableCell>
                  <TableCell className="text-center"><Switch checked={em} onCheckedChange={v=>toggleMonetaria(c.id,v)}/></TableCell>
                  <TableCell className="text-center">{em?<span className="text-slate-400 text-xs">No (RECPAM)</span>:<Badge className="bg-blue-100 text-blue-700 text-xs">Sí</Badge>}</TableCell>
                </TableRow>);
              })}</TableBody></Table></div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CÁLCULO RT54 */}
        <TabsContent value="calculo" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calculator className="h-5 w-5 text-blue-600"/>Ejecutar Cálculo RT54</CardTitle>
              <CardDescription>Seleccione el ejercicio para calcular el ajuste por inflación</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end">
                <div className="flex-1 max-w-xs">
                  <Label>Ejercicio</Label>
                  <Select value={ejercicioSeleccionado} onValueChange={setEjercicioSeleccionado}>
                    <SelectTrigger data-testid="select-ejercicio"><SelectValue placeholder="Seleccionar ejercicio"/></SelectTrigger>
                    <SelectContent>{ejercicios.map(e=><SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={ejecutarCalculo} disabled={calculando||!ejercicioSeleccionado} data-testid="btn-calcular">
                  {calculando?<RefreshCw className="h-4 w-4 animate-spin mr-2"/>:<PlayCircle className="h-4 w-4 mr-2"/>}
                  {calculando?"Calculando...":"Calcular Ajuste"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                El cálculo toma todos los asientos confirmados del ejercicio, aplica coeficientes a las partidas no monetarias y calcula el RECPAM para las monetarias.
              </p>
            </CardContent>
          </Card>

          {/* Historial */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Historial de Cálculos</CardTitle></CardHeader>
            <CardContent className="p-0">
              {resultados.length===0?(
                <div className="text-center py-12 text-slate-500"><Calculator className="h-10 w-10 mx-auto mb-2 opacity-40"/><p>No hay cálculos realizados</p></div>
              ):(
                <Table data-testid="tabla-resultados">
                  <TableHeader><TableRow>
                    <TableHead>#</TableHead><TableHead>Ejercicio</TableHead><TableHead>Fecha Cálculo</TableHead>
                    <TableHead>Cierre</TableHead><TableHead className="text-right">Índice</TableHead>
                    <TableHead className="text-right">Ajuste NM</TableHead><TableHead className="text-right">RECPAM</TableHead>
                    <TableHead>Estado</TableHead><TableHead>Asiento</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{resultados.map(r=>(
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-sm">{r.id}</TableCell>
                      <TableCell className="font-medium text-sm">{r.ejercicio_nombre}</TableCell>
                      <TableCell className="text-sm text-slate-500">{r.fecha_calculo?.split('.')[0]}</TableCell>
                      <TableCell className="text-sm">{fmtP(r.periodo_cierre)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtN(r.indice_cierre)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtC(r.total_ajuste_no_monetarias)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <span className={r.recpam>0?'text-red-600':r.recpam<0?'text-green-600':''}>{fmtC(r.recpam)}</span>
                      </TableCell>
                      <TableCell><Badge className={r.estado==='CONFIRMADO'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}>{r.estado}</Badge></TableCell>
                      <TableCell>{r.asiento_id?<Badge className="bg-green-100 text-green-700 text-xs">#{r.asiento_id}</Badge>:<span className="text-slate-400 text-xs">-</span>}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" onClick={()=>verResultado(r.id)} data-testid={`btn-ver-resultado-${r.id}`}><Eye className="h-3.5 w-3.5"/></Button></TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EVOLUCIÓN */}
        <TabsContent value="evolucion" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Evolución Índice FACPCE</CardTitle></CardHeader>
              <CardContent>{indicesChart.length>0?(<ResponsiveContainer width="100%" height={300}>
                <LineChart data={indicesChart}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="periodo" tick={{fontSize:10}} angle={-45} textAnchor="end" height={60}/>
                <YAxis tick={{fontSize:11}}/><Tooltip formatter={v=>fmtN(v)}/>
                <Line type="monotone" dataKey="indice" stroke="#3b82f6" strokeWidth={2} dot={{r:2}} name="Índice"/>
              </LineChart></ResponsiveContainer>):<p className="text-center py-12 text-slate-400">Sin datos</p>}</CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Variación Mensual (%)</CardTitle></CardHeader>
              <CardContent>{indicesChart.length>0?(<ResponsiveContainer width="100%" height={300}>
                <BarChart data={indicesChart}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                <XAxis dataKey="periodo" tick={{fontSize:10}} angle={-45} textAnchor="end" height={60}/>
                <YAxis tick={{fontSize:11}} tickFormatter={v=>`${v}%`}/><Tooltip formatter={v=>`${v}%`}/>
                <Bar dataKey="variacion" name="Var. Mensual" fill="#f59e0b" radius={[3,3,0,0]}/>
              </BarChart></ResponsiveContainer>):<p className="text-center py-12 text-slate-400">Sin datos</p>}</CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Índice */}
      <Dialog open={modalIndice} onOpenChange={setModalIndice}>
        <DialogContent className="max-w-sm" data-testid="modal-indice">
          <DialogHeader><DialogTitle>{editingIndice?"Editar Índice":"Agregar Índice FACPCE"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Período</Label><Input type="month" value={formIndice.periodo} disabled={!!editingIndice} onChange={e=>setFormIndice({...formIndice,periodo:e.target.value})}/></div>
            <div><Label>Índice</Label><Input type="number" step="0.01" value={formIndice.indice} onChange={e=>setFormIndice({...formIndice,indice:e.target.value})} placeholder="9882.15"/></div>
            <div><Label>Var. Mensual % (auto si vacío)</Label><Input type="number" step="0.01" value={formIndice.variacion_mensual} onChange={e=>setFormIndice({...formIndice,variacion_mensual:e.target.value})}/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setModalIndice(false)}>Cancelar</Button>
            <Button onClick={handleGuardarIndice} disabled={saving} data-testid="btn-guardar-indice">
              {saving?<RefreshCw className="h-4 w-4 animate-spin mr-1"/>:<Save className="h-4 w-4 mr-1"/>}{editingIndice?"Actualizar":"Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AjusteInflacion;
